import { useState, useRef } from 'react';
import { Upload, X, AlertTriangle, Loader2, Check, BookMarked, Download, ImageIcon, ScanLine } from 'lucide-react';
import { extractPdfCover } from '@/lib/pdfCover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { uploadWithProgress, uploadFileToAllServers, formatBytes } from '@/lib/blossomUpload';
import { KIND_EBOOK } from '@/lib/pressstrKinds';

interface UploadEbookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface UploadProgress {
  stage: 'idle' | 'uploading-file' | 'uploading-cover' | 'publishing' | 'complete' | 'error';
  message: string;
  progress: number;
  fileLoaded?: number;
  fileTotal?: number;
  error?: string;
}

function slugify(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

const ACCEPTED_EBOOK_TYPES = ['application/pdf', 'application/epub+zip'];

export function UploadEbookDialog({ open, onOpenChange, onSuccess }: UploadEbookDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutateAsync: publishEvent } = useNostrPublish();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [authors, setAuthors] = useState<string[]>([]);
  const [authorInput, setAuthorInput] = useState('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [ebookFile, setEbookFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverAutoExtracted, setCoverAutoExtracted] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isbn, setIsbn] = useState('');
  const [language, setLanguage] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({ stage: 'idle', message: '', progress: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManual) setSlug(slugify(value));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_EBOOK_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file', description: 'Please select a PDF or EPUB file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setEbookFile(cloned);
    e.target.value = '';

    // Auto-extract cover from first page of PDF
    if (file.type === 'application/pdf') {
      await doExtractCover(cloned);
    }
  };

  const doExtractCover = async (pdfSource?: File) => {
    const src = pdfSource ?? ebookFile;
    if (!src) {
      toast({ title: 'No PDF selected', description: 'Select a PDF first.', variant: 'destructive' });
      return;
    }
    if (src.type !== 'application/pdf') {
      toast({ title: 'Not a PDF', description: 'Cover extraction only works with PDF files.', variant: 'destructive' });
      return;
    }
    setIsExtracting(true);
    try {
      const cover = await extractPdfCover(src);
      setCoverFile(cover);
      setCoverPreview(URL.createObjectURL(cover));
      setCoverAutoExtracted(true);
    } catch (err) {
      toast({
        title: 'Cover extraction failed',
        description: err instanceof Error ? err.message : 'Could not render PDF page 1.',
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleCoverSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setCoverFile(cloned);
    setCoverPreview(URL.createObjectURL(cloned));
    e.target.value = '';
  };

  const addAuthor = () => {
    const a = authorInput.trim();
    if (a && !authors.includes(a)) {
      setAuthors([...authors, a]);
      setAuthorInput('');
    }
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const handleUpload = async () => {
    if (!ebookFile || !title.trim()) {
      toast({ title: 'Missing fields', description: 'Title and ebook file are required.', variant: 'destructive' });
      return;
    }
    if (!user) {
      toast({ title: 'Login required', description: 'You must be logged in to upload ebooks.', variant: 'destructive' });
      return;
    }

    const servers = getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers);
    if (servers.length === 0) {
      toast({ title: 'No Blossom servers', description: 'Configure Blossom servers in settings.', variant: 'destructive' });
      return;
    }

    setUploadProgress({ stage: 'uploading-file', message: 'Uploading ebook to Blossom...', progress: 10, fileLoaded: 0, fileTotal: ebookFile.size });

    try {
      const fileResult = await uploadWithProgress(
        ebookFile,
        servers,
        user.signer,
        ({ loaded, total }) => {
          setUploadProgress(prev => ({ ...prev, fileLoaded: loaded, fileTotal: total }));
        },
      );

      let coverUrl: string | undefined;
      if (coverFile) {
        setUploadProgress({ stage: 'uploading-cover', message: 'Uploading cover image...', progress: 75 });
        const coverResult = await uploadFileToAllServers(coverFile, servers, user.signer);
        coverUrl = coverResult.url;
      }

      setUploadProgress({ stage: 'publishing', message: 'Publishing to Nostr...', progress: 90 });

      const dTag = slug.trim() || slugify(title.trim());
      const eventTags: string[][] = [
        ['d', dTag],
        ['title', title.trim()],
        ['url', fileResult.url],
        ['m', ebookFile.type],
        ['size', String(ebookFile.size)],
        ['x', fileResult.sha256],
        ['alt', `Ebook: ${title.trim()}`],
      ];

      if (coverUrl) eventTags.push(['image', coverUrl]);
      for (const a of authors) eventTags.push(['author', a]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (language.trim()) eventTags.push(['l', language.trim().toLowerCase()]);
      if (isbn.trim()) eventTags.push(['isbn', isbn.trim()]);
      for (const t of tags) eventTags.push(['t', t]);
      if (publishedAt) {
        const ts = Math.floor(new Date(publishedAt).getTime() / 1000);
        if (!isNaN(ts)) eventTags.push(['published_at', String(ts)]);
      }

      await publishEvent({
        kind: KIND_EBOOK,
        content: description.trim(),
        tags: eventTags,
      });

      setUploadProgress({ stage: 'complete', message: 'Ebook published!', progress: 100 });
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setUploadProgress({ stage: 'error', message: 'Upload failed', progress: 0, error: msg });
      toast({ title: 'Upload failed', description: msg, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setSlugManual(false);
    setAuthors([]);
    setAuthorInput('');
    setSummary('');
    setDescription('');
    setEbookFile(null);
    setCoverFile(null);
    setCoverPreview(null);
    setCoverAutoExtracted(false);
    setIsExtracting(false);
    setIsbn('');
    setLanguage('');
    setPublishedAt('');
    setTagInput('');
    setTags([]);
    setUploadProgress({ stage: 'idle', message: '', progress: 0 });
  };

  const handleClose = () => {
    if (uploadProgress.stage === 'uploading-file' || uploadProgress.stage === 'uploading-cover' || uploadProgress.stage === 'publishing') return;
    resetForm();
    onOpenChange(false);
  };

  // Progress view
  if (uploadProgress.stage !== 'idle') {
    return (
      <Dialog open={open} onOpenChange={uploadProgress.stage === 'complete' || uploadProgress.stage === 'error' ? handleClose : undefined}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookMarked className="w-5 h-5" />
              {uploadProgress.stage === 'complete' ? 'Ebook Published!' : uploadProgress.stage === 'error' ? 'Upload Failed' : 'Uploading Ebook...'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {uploadProgress.stage !== 'complete' && uploadProgress.stage !== 'error' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{uploadProgress.message}</span>
                    <span className="font-medium">{uploadProgress.progress}%</span>
                  </div>
                  <Progress value={uploadProgress.progress} />
                </div>

                {uploadProgress.fileTotal !== undefined && uploadProgress.fileTotal > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Upload className="w-3 h-3" />
                        File upload
                      </span>
                      <span className="tabular-nums">
                        {formatBytes(uploadProgress.fileLoaded ?? 0)} / {formatBytes(uploadProgress.fileTotal)}
                        {' '}
                        <span className="text-foreground font-medium">
                          ({Math.round(((uploadProgress.fileLoaded ?? 0) / uploadProgress.fileTotal) * 100)}%)
                        </span>
                      </span>
                    </div>
                    <Progress
                      value={Math.round(((uploadProgress.fileLoaded ?? 0) / uploadProgress.fileTotal) * 100)}
                      className="h-2"
                    />
                  </div>
                )}
              </div>
            )}

            {uploadProgress.stage === 'error' && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{uploadProgress.error}</AlertDescription>
              </Alert>
            )}

            {uploadProgress.stage === 'complete' && (
              <Alert className="border-green-500 bg-green-500/10">
                <Check className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-700 dark:text-green-400">
                  The ebook has been published to Nostr!
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              {uploadProgress.stage === 'complete' && <Button className="flex-1" onClick={handleClose}>Close</Button>}
              {uploadProgress.stage === 'error' && (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => setUploadProgress({ stage: 'idle', message: '', progress: 0 })}>Try Again</Button>
                  <Button variant="outline" onClick={handleClose}>Cancel</Button>
                </>
              )}
              {uploadProgress.stage !== 'complete' && uploadProgress.stage !== 'error' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground w-full justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Please wait, do not close this window...
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="w-5 h-5" />
            Upload Ebook
          </DialogTitle>
          <DialogDescription>
            Publish an ebook (PDF or EPUB) to Nostr via Blossom file hosting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* File Upload */}
          <div>
            <Label className="text-sm font-medium">Ebook File (PDF or EPUB) *</Label>
            <input ref={fileInputRef} type="file" accept="application/pdf,application/epub+zip" onChange={handleFileSelect} className="hidden" />
            {ebookFile ? (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{ebookFile.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(ebookFile.size)} · {ebookFile.type === 'application/epub+zip' ? 'EPUB' : 'PDF'}</p>
                </div>
                <button onClick={() => setEbookFile(null)} className="p-1 hover:text-destructive">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-2 w-full border-2 border-dashed rounded-xl p-8 text-center hover:border-primary hover:bg-muted/50 transition-colors"
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to select a PDF or EPUB file</p>
              </button>
            )}
          </div>

          {/* Cover */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {coverPreview ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button onClick={() => { setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); }} className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button onClick={() => coverInputRef.current?.click()} className="w-20 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div className="flex-1 pt-1 space-y-2">
                {coverAutoExtracted && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Extracted from PDF page 1
                  </p>
                )}
                <p className="text-xs text-muted-foreground">Portrait 2:3 cover image.</p>
                <div className="flex flex-wrap gap-2">
                  {ebookFile?.type === 'application/pdf' && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={isExtracting}
                      onClick={() => doExtractCover()}
                    >
                      {isExtracting
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Extracting…</>
                        : <><ScanLine className="w-3 h-3 mr-1" />Extract from PDF</>
                      }
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    Upload image
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="ebook-title">Title *</Label>
            <Input id="ebook-title" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="The Bitcoin Standard" className="mt-1.5" />
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="ebook-slug">Slug</Label>
            <Input id="ebook-slug" value={slug} onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }} placeholder="the-bitcoin-standard" className="mt-1.5 font-mono text-sm" />
          </div>

          {/* Authors */}
          <div>
            <Label>Authors</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={authorInput} onChange={(e) => setAuthorInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAuthor())} placeholder="Author name" />
              <Button type="button" variant="secondary" onClick={addAuthor}>Add</Button>
            </div>
            {authors.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {authors.map(a => (
                  <Badge key={a} variant="secondary" className="pl-2">
                    {a}
                    <button onClick={() => setAuthors(authors.filter(x => x !== a))} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <div>
            <Label htmlFor="ebook-summary">Short Summary</Label>
            <Input id="ebook-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Brief description / back cover blurb" className="mt-1.5" />
          </div>

          {/* Full description */}
          <div>
            <Label htmlFor="ebook-desc">Full Description (Markdown)</Label>
            <Textarea id="ebook-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Extended description..." rows={3} className="mt-1.5" />
          </div>

          {/* ISBN */}
          <div>
            <Label htmlFor="ebook-isbn">ISBN-13</Label>
            <Input id="ebook-isbn" value={isbn} onChange={(e) => setIsbn(e.target.value)} placeholder="978-0000000000" className="mt-1.5 w-48" />
          </div>

          {/* Language */}
          <div>
            <Label htmlFor="ebook-lang">Language</Label>
            <Input id="ebook-lang" value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="en, de, fr..." className="mt-1.5 w-32" maxLength={5} />
          </div>

          {/* Publication date */}
          <div>
            <Label htmlFor="ebook-date">Original Publication Date</Label>
            <Input id="ebook-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className="mt-1.5 w-48" />
          </div>

          {/* Topics */}
          <div>
            <Label>Topics / Tags</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="bitcoin, fiction, economics..." />
              <Button type="button" variant="secondary" onClick={addTag}>Add</Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map(t => (
                  <Badge key={t} variant="secondary" className="pl-2">
                    #{t}
                    <button onClick={() => setTags(tags.filter(x => x !== t))} className="ml-1 hover:text-destructive"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleUpload} className="flex-1" disabled={!ebookFile || !title.trim()}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Ebook
            </Button>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
